import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Transform } from '@dnd-kit/utilities/dist/css';
import { ListItem, styled, Theme, CSSProperties } from '@nexelem/deprecated-theme';
import { FC } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines } from '@fortawesome/pro-light-svg-icons';

type StyledListItemProps = {
  readonly transform: Transform | null;
  readonly transition?: string;
  readonly isHidden?: boolean;
};
const StyledSortableListItem = styled(ListItem, {
  shouldForwardProp: (propName: string) => !['isDragging', 'isHidden'].includes(propName),
})<StyledListItemProps & { isDragging: boolean }>(({ transform, isHidden, transition, theme, isDragging }) => {
  const typedTheme = theme as Theme;
  const { getColor, hexToRGB } = typedTheme.palette;
  const { sortableListItem } = typedTheme.client.lists;
  const defaultStyles = {
    ...typedTheme.typography.titilliumWebSemiBold16,
    alignItems: 'center',
    justifyItems: 'space-between',
    backgroundColor: getColor(
      isHidden ? sortableListItem.hidden.backgroundColor : sortableListItem.defaultState.backgroundColor,
    ),
    color: getColor(isHidden ? sortableListItem.hidden.color : sortableListItem.defaultState.color),
    cursor: isHidden ? 'inherit' : 'move',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 0),
    borderRadius: theme.spacing(1),
    border: `1px solid ${getColor('coolGrey10')}`,
    // TBD: tested and with none better working on phone
    // https://docs.dndkit.com/api-documentation/sensors/touch
    // touchAction: 'manipulation',
    padding: 0,
    touchAction: 'none',
    transform: CSS.Transform.toString(transform),
    transition,
    '.action': {
      all: 'unset',
      outline: 'inherit',
      cursor: 'pointer',
      padding: theme.spacing(3, 4, 3, 4),
      color: getColor(isHidden ? sortableListItem.hidden.color : sortableListItem.defaultState.actionsColor),
      '&:hover': {
        backgroundColor: hexToRGB(getColor(sortableListItem.hover.backgroundColor), 0.5),
      },
    },
    '.description': {
      display: 'flex',
      flexGrow: '1',
      alignItems: 'center',
      padding: theme.spacing(3, 0, 3, 2),
      svg: {
        color: getColor(sortableListItem.defaultState.icon),
        paddingRight: theme.spacing(2),
        width: '1em',
        height: '1em',
        fontSize: '20px',
      },
    },
  };

  if (!isHidden) {
    return {
      ...defaultStyles,
      '.description': {
        ...defaultStyles['.description'],
        span: {
          color: isDragging ? getColor(sortableListItem.dragging.color) : getColor(sortableListItem.defaultState.color),
        },
      },
      ...(isDragging ? theme.palette.elevation.small : {}),
      '&:active': {
        backgroundColor: getColor(
          isDragging ? sortableListItem.dragging.backgroundColor : sortableListItem.active.backgroundColor,
        ),
        color: getColor(sortableListItem.active.color),
      },
      '&:hover:not(:has(.action:hover))': {
        backgroundColor: isDragging
          ? getColor(sortableListItem.dragging.backgroundColor)
          : hexToRGB(getColor(sortableListItem.hover.backgroundColor), 0.5),
        color: getColor(sortableListItem.hover.color),
        '.action': {
          color: getColor(sortableListItem.hover.actionsColor),
        },
      },
    } as CSSProperties;
  }
  return defaultStyles as CSSProperties;
});

export type ActionsProps = {
  readonly className: string;
};

export type SortableItemListProps = {
  readonly id: string | number;
  readonly description: string;
  readonly isHidden?: boolean;
  readonly Actions?: FC<ActionsProps>;
  readonly className?: string;
};

/***
 * Important note: id shouldn't be false
 */
export function SortableListItem({ id, description, isHidden, className, Actions }: SortableItemListProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
    id,
    disabled: isHidden,
  });

  return (
    <StyledSortableListItem
      className={className}
      isDragging={isDragging}
      isHidden={isHidden}
      ref={setNodeRef}
      transform={transform}
      transition={transition}
      {...attributes}
      tabIndex={0}
    >
      <div className="description" ref={setActivatorNodeRef} {...(isHidden ? {} : listeners)}>
        <FontAwesomeIcon icon={faGripLines} />
        <span>{description}</span>
      </div>
      {Actions && <Actions className="action" />}
    </StyledSortableListItem>
  );
}
